/**
 * „Stały link do rozmów" in Integracje → Kalendarz (owner, 28 October 2026): the link every video
 * consultation takes when Google makes no Meet. Its host only — the link joins whoever holds it
 * (consultation-calls R7) — „Zmień" / „Usuń", or the empty state's platform suggestion.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormBanner } from '~/shared/ui/FormBanner';
import { FormField } from '~/shared/ui/FormField';
import ghost from '~/shared/ui/ghostButton.module.css';
import ink from '~/shared/ui/inkButton.module.css';

import type { StandingCallLinkApi } from '../lib/useStandingCallLink';
import screen from './AccountScreen.module.css';
import styles from './CalendarTab.module.css';

const KEY = 'web:account.calendar.standing';
const INK = `${String(ink.ink)} ${String(screen.accountButton)} ${String(screen.accountButtonBold)}`;
const GHOST = `${String(ghost.ghost)} ${String(screen.accountButton)} ${String(screen.canvasGhost)}`;

export function StandingCallLinkBlock({
  standing,
  className,
}: {
  readonly standing: StandingCallLinkApi;
  readonly className: string;
}) {
  const { t } = useTranslation();
  const { view, field } = standing;
  const fieldBox = useRef<HTMLFormElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const fieldOpen = field !== null;
  const wasOpen = useRef(false);

  // Into the field when it opens; back to the act that opened it when it closes.
  useEffect(() => {
    if (fieldOpen) fieldBox.current?.querySelector('input')?.focus();
    else if (wasOpen.current) opener.current?.focus();
    wasOpen.current = fieldOpen;
  }, [fieldOpen]);

  if (view === null) return null;

  return (
    <div className={`${String(screen.block)} ${className}`} data-clarity-mask="True">
      <h3 className={screen.blockTitle}>{t(`${KEY}.title`)}</h3>
      {field !== null ? (
        <form
          ref={fieldBox}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            standing.onSave();
          }}
        >
          <FormField
            label={t(`${KEY}.fieldLabel`)}
            value={field.value}
            placeholder="https://"
            error={field.problem}
            onChange={standing.onEdit}
          />
          <div className={styles.actions}>
            <button type="submit" className={INK} aria-disabled={standing.busy ? 'true' : undefined}>
              {t(`${KEY}.save`)}
            </button>
            <button type="button" className={GHOST} onClick={standing.onClose}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <>
          {view.kind === 'set' ? (
            <b className={screen.rowTitle}>{view.host}</b>
          ) : (
            <span className={screen.rowNote}>{t(view.bodyKey)}</span>
          )}
          <div className={styles.actions}>
            <button
              ref={opener}
              type="button"
              className={view.kind === 'set' ? GHOST : INK}
              aria-disabled={standing.busy ? 'true' : undefined}
              onClick={standing.onOpen}
            >
              {t(view.kind === 'set' ? `${KEY}.change` : `${KEY}.add`)}
            </button>
            {view.kind === 'set' ? (
              <button
                type="button"
                className={GHOST}
                aria-disabled={standing.busy ? 'true' : undefined}
                onClick={standing.onRemove}
              >
                {t(`${KEY}.remove`)}
              </button>
            ) : null}
          </div>
        </>
      )}
      <FormBanner message={standing.refusal} />
    </div>
  );
}
